<!--
  ReviewScheduleEditDialog — Dialog for creating review assignments.

  Used by:
  - ReviewAssignments.vue — "add" and "duplicate" modes from table row actions
  - ReviewCalendarPage.vue — "add", "edit", and "duplicate" modes from calendar events

  Modes (controlled by the `mode` prop):
  - "add":       Create new assignments. Admin picks platforms (multi), location(s),
                 brands (multi), user, date range → backend picks eligible reviews.
                 Supports multiple locations in add mode.
  - "edit":      Edit an existing assignment. Pre-fills form from entry. Deletes old
                 assignment and creates a replacement on save.
  - "duplicate": Create new assignments pre-populated from an existing entry.
                 Supports multiple locations like add mode.

  Props:
  - modelValue:  v-model binding for open/close state (Boolean)
  - mode:        Which mode to render — "add" | "edit" | "duplicate"
  - entry:       The source assignment entry, used in "edit" / "duplicate" mode

  Events:
  - update:modelValue  — emitted to open/close the dialog (v-model support)
  - saved              — emitted after a successful save so the parent can refresh its data
-->
<template>
  <BaseDialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="dialogTitle"
    persistent
    :save-text="mode === 'edit' ? 'Save' : 'Create'"
    :save-loading="saving"
    :save-disabled="!isValid"
    cancel-text="Cancel"
    @save="save"
  >
    <!-- Date range for the assignment period -->
    <v-row density="compact" class="mb-1">
      <v-col cols="6">
        <v-text-field
          v-model="form.startDate"
          label="Date From"
          type="date"
          density="comfortable"
          variant="outlined"
          hide-details
          @update:model-value="onStartDateChange"
        />
      </v-col>
      <v-col cols="6">
        <v-text-field
          v-model="form.endDate"
          label="Date To"
          type="date"
          density="comfortable"
          variant="outlined"
          hide-details
          :min="form.startDate"
        />
      </v-col>
    </v-row>

    <!-- Platform dropdown with color swatch — multi-select -->
    <v-select
      v-model="form.platforms"
      :items="options.platforms"
      label="Platform(s)"
      density="comfortable"
      variant="outlined"
      hide-details
      class="mb-3"
      multiple
      chips
      closable-chips
    >
      <!-- Color swatch in the dropdown list item -->
      <template v-slot:item="{ item, props: itemProps }">
        <v-list-item v-bind="itemProps">
          <template v-slot:prepend>
            <div :style="{ background: getPlatformGradient(item), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }" />
          </template>
        </v-list-item>
      </template>
    </v-select>

    <!-- City + Location on the same row -->
    <v-row density="compact" class="mb-1">
      <v-col cols="5">
        <!-- City dropdown — filters the location list -->
        <v-select
          v-model="form.city"
          :items="options.cities"
          label="City"
          density="comfortable"
          variant="outlined"
          hide-details
        />
      </v-col>
      <v-col cols="7">
        <!-- Location dropdown — single in edit mode, multi in add/duplicate mode -->
        <v-select
          v-model="form.location"
          :items="filteredLocationNames"
          :label="isMultiLocationMode ? 'Location(s)' : 'Location'"
          density="comfortable"
          variant="outlined"
          hide-details
          :disabled="!form.city"
          :multiple="isMultiLocationMode"
          :chips="isMultiLocationMode"
          :closable-chips="isMultiLocationMode"
        />
      </v-col>
    </v-row>

    <!-- Brand dropdown — multi-select with chips -->
    <v-select
      v-model="form.brands"
      :items="options.brands"
      label="Brand(s)"
      density="comfortable"
      variant="outlined"
      hide-details
      class="mb-3"
      multiple
      chips
      closable-chips
    />

    <!-- Assigned user dropdown with email subtitle -->
    <v-select
      v-model="form.user_id"
      :items="userItems"
      item-title="name"
      item-value="id"
      label="Assigned User"
      density="comfortable"
      variant="outlined"
      hide-details
      class="mb-3"
    >
      <!-- Custom item rendering to show email as subtitle -->
      <template v-slot:item="{ internalItem, props: itemProps }">
        <v-list-item v-bind="itemProps" :subtitle="internalItem.raw?.email" />
      </template>
    </v-select>

    <!-- Reason + Num Reviews on the same row -->
    <v-row density="compact" class="mb-1">
      <v-col cols="8">
        <!-- Reason: Baseline (proactive) or Response (reactive to a customer review) -->
        <v-btn-toggle
          v-model="form.reason"
          mandatory
          variant="outlined"
          density="compact"
          color="primary"
        >
          <v-btn value="Baseline">Baseline</v-btn>
          <v-btn value="Response">Response</v-btn>
        </v-btn-toggle>
      </v-col>
      <v-col cols="4">
        <!-- Number of reviews per combo per day -->
        <v-text-field
          v-model.number="form.num_reviews"
          label="Num Reviews"
          type="number"
          min="1"
          density="comfortable"
          variant="outlined"
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Email notification option: when to send -->
    <v-select
      v-model="form.sendEmail"
      :items="emailOptions"
      item-title="label"
      item-value="value"
      label="Send email notification"
      density="comfortable"
      variant="outlined"
      hide-details
      class="mb-1"
    />

    <!-- Warnings from the backend (e.g. "all reviews on cooldown for X combo") -->
    <v-alert v-if="saveWarnings.length > 0" type="warning" variant="tonal" density="compact" class="mt-2">
      <div v-for="(w, i) in saveWarnings" :key="i" class="text-body-2">{{ w }}</div>
    </v-alert>

    <!-- Error from the backend -->
    <v-alert v-if="saveError" type="error" variant="tonal" density="compact" class="mt-2">
      {{ saveError }}
    </v-alert>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import BaseDialog from "@/components/BaseDialog.vue";
import api from "@/utils/api.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const { getPlatformGradient } = usePlatformColors();

const props = defineProps({
  modelValue: Boolean,
  mode: { type: String, default: "add" },
  entry: { type: Object, default: null },
  prefill: { type: Object, default: null }, // { startDate, endDate } when adding from calendar day click
});

const emit = defineEmits(["update:modelValue", "saved"]);

// Whether a save is in progress
const saving = ref(false);

// Options for the "Send email notification" dropdown
const emailOptions = [
  { value: "never", label: "Never" },
  { value: "day_of", label: "On assignment date (daily cron)" },
  { value: "immediate", label: "Immediately" },
];

// Cached dropdown option lists fetched from the sales options API
const options = ref({ platforms: [], cities: [], locations: [], brands: [] });

// Full objects with IDs — used for cascading city→location filtering
const allLocations = ref([]);
const allCities = ref([]);

// Cached list of users eligible to be assigned reviews
const userItems = ref([]);

// Guard flag — options are fetched once on first open and reused on subsequent opens
const optionsLoaded = ref(false);

// Warnings and errors from the last save attempt
const saveWarnings = ref([]);
const saveError = ref("");

// Multi-location mode: enabled in add and duplicate modes, single in edit
const isMultiLocationMode = computed(() => props.mode !== "edit");

// Filter locations by selected city — only shows locations belonging to that city
const filteredLocationNames = computed(() => {
  if (!form.value.city) return options.value.locations;
  // Find the city_id that matches the selected city name
  const cityObj = allCities.value.find((c) => (c.name || c) === form.value.city);
  if (!cityObj?.id) return options.value.locations;
  // Return only location names whose city_id matches
  return allLocations.value
    .filter((l) => l.city_id === cityObj.id)
    .map((l) => l.name || l);
});

// Reactive form state
const form = ref(getDefaultForm());

// Dialog title
const dialogTitle = computed(() => {
  if (props.mode === "edit") return "Edit Assignment";
  if (props.mode === "duplicate") return "Duplicate Assignment";
  return "Add Assignment";
});

// Validation — the Save button is disabled until this is true
const isValid = computed(() => {
  const f = form.value;
  // In multi-location mode, location is an array; otherwise a single string
  const hasLocation = isMultiLocationMode.value
    ? f.location?.length > 0
    : !!f.location;
  return (
    f.startDate &&
    f.endDate &&
    f.platforms?.length > 0 &&
    hasLocation &&
    f.brands?.length > 0 &&
    f.user_id &&
    f.num_reviews > 0
  );
});

/**
 * Returns an empty form object with sensible defaults.
 */
function getDefaultForm() {
  return {
    startDate: "",
    endDate: "",
    platforms: [],
    city: null,
    location: null, // Single string in edit mode, array in add/duplicate
    brands: [],
    user_id: null,
    num_reviews: 1,
    sendEmail: "never", // Default: don't send email — user opts in
    reason: "Baseline", // Which type of review to pick: Baseline or Response
  };
}

// Watch for the dialog being opened (modelValue flips to true).
// On open: load dropdown options if not yet fetched, then reset the form.
watch(
  () => props.modelValue,
  async (open) => {
    if (!open) return;

    // Clear warnings/errors from previous save
    saveWarnings.value = [];
    saveError.value = "";

    // Fetch platforms, locations, brands, and users on first open — cached afterwards
    if (!optionsLoaded.value) {
      await loadOptions();
    }

    // Populate form based on mode.
    // Skip the city watcher during form init to avoid clearing the pre-filled location.
    if ((props.mode === "edit" || props.mode === "duplicate") && props.entry) {
      skipCityWatch = true;
      // Edit or duplicate: pre-fill from the source entry
      // schedule_date is the compat-mapped field from useReviewSchedule; fall back to assignment_date
      const dateRaw = props.entry.schedule_date || props.entry.assignment_date;
      const d = typeof dateRaw === "string" ? dateRaw.slice(0, 10) : "";
      // assigned_user_id is the compat-mapped field; fall back to user_id
      const userId = props.entry.assigned_user_id || props.entry.user_id;
      // Resolve city from the entry's location by looking up city_id
      const locObj = allLocations.value.find((l) => (l.name || l) === props.entry.location);
      const cityName = locObj ? allCities.value.find((c) => c.id === locObj.city_id)?.name || null : null;

      // In edit mode, location is a single string; in duplicate, it's an array
      const locationValue = props.mode === "edit"
        ? props.entry.location
        : [props.entry.location];

      form.value = {
        startDate: d,
        endDate: d,
        platforms: [props.entry.platform],
        city: cityName,
        location: locationValue,
        brands: [props.entry.brand],
        user_id: userId,
        num_reviews: props.entry.num_reviews || 1,
        sendEmail: "never", // Default off — user opts in
        reason: props.entry.reason || "Baseline",
      };
    } else {
      // Add mode: start with defaults, apply prefill if provided (e.g. day click)
      const today = new Date().toISOString().slice(0, 10);
      form.value = {
        startDate: props.prefill?.startDate || today,
        endDate: props.prefill?.endDate || today,
        platforms: [],
        city: null,
        location: [], // Array for multi-location mode
        brands: [],
        user_id: null,
        num_reviews: 1,
        sendEmail: "never", // Default off — user opts in
      };
    }
  }
);

/**
 * Loads dropdown option lists from the API.
 */
async function loadOptions() {
  try {
    const [salesRes, usersRes] = await Promise.all([
      api.accountingApi.get("/api/accounting/sales/options"),
      api.authApi.get("/api/admin/users"),
    ]);

    const salesOpts = salesRes.data || {};
    // Preserve full objects (with IDs) for cascading city→location filtering
    allLocations.value = salesOpts.locations || [];
    allCities.value = salesOpts.cities || [];
    options.value = {
      platforms: (salesOpts.platforms || []).map((p) => p.name || p),
      cities: allCities.value.map((c) => c.name || c),
      locations: allLocations.value.map((l) => l.name || l),
      brands: (salesOpts.brands || []).map((b) => b.name || b),
    };

    // Filter users to only those who can be assigned reviews
    const users = usersRes.data || [];
    userItems.value = users
      .filter(
        (u) =>
          u.account_status === "active" &&
          u.allowed_apps &&
          (Array.isArray(u.allowed_apps)
            ? u.allowed_apps.includes("reviews")
            : JSON.parse(u.allowed_apps || "[]").includes("reviews"))
      )
      .map((u) => ({
        id: u.id,
        name: u.first_name
          ? `${u.first_name}${u.last_name ? " " + u.last_name : ""}`
          : u.email,
        email: u.email,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));

    optionsLoaded.value = true;
  } catch (err) {
    console.error("Failed to load dialog options:", err);
  }
}

// Clear location when city changes — prevents stale city/location combos.
// Skip during form initialization (the modelValue watcher sets city+location together).
let skipCityWatch = false;
watch(
  () => form.value.city,
  () => {
    if (skipCityWatch) {
      skipCityWatch = false;
      return;
    }
    // Reset location to appropriate empty value based on mode
    form.value.location = isMultiLocationMode.value ? [] : null;
  }
);

/**
 * Auto-sync handler for the start date input.
 * When the user sets a start date, the end date is automatically advanced
 * to match if the current end date is empty or earlier.
 */
function onStartDateChange(val) {
  if (val && (!form.value.endDate || form.value.endDate < val)) {
    form.value.endDate = val;
  }
}

/**
 * Saves the form data to the backend.
 *
 * In multi-location mode (add/duplicate), calls the API once per location
 * since each location generates independent combos.
 *
 * In edit mode, bulk-deletes the old assignment then creates a single replacement.
 */
async function save() {
  saving.value = true;
  saveWarnings.value = [];
  saveError.value = "";

  try {
    // Normalize location to an array for uniform handling
    const locations = isMultiLocationMode.value
      ? (form.value.location || [])
      : [form.value.location];

    let totalCreated = 0;
    const allWarnings = [];

    // Create assignments for each location FIRST — before deleting existing ones.
    // This way, if creation fails (e.g. no eligible reviews), the old assignments
    // are preserved and the user doesn't lose their data.
    for (const location of locations) {
      const res = await api.marketingApi.post("/api/marketing/reviews/admin/assignments", {
        platforms: form.value.platforms,
        location,
        brands: form.value.brands,
        user_id: form.value.user_id,
        startDate: form.value.startDate,
        endDate: form.value.endDate,
        num_reviews: form.value.num_reviews,
        sendEmail: form.value.sendEmail,
        reason: form.value.reason,
      });

      totalCreated += res.data.created || 0;
      if (res.data.warnings?.length) {
        allWarnings.push(...res.data.warnings);
      }
    }

    // Creation succeeded — now safe to delete the old assignments.
    // Uses allIds (array of every assignmentId in the combo) so that reducing
    // num_reviews correctly removes the extra assignments.
    if (props.mode === "edit" && props.entry) {
      const ids = props.entry.allIds || (props.entry.id ? [props.entry.id] : []);
      if (ids.length > 0) {
        await api.marketingApi.post("/api/marketing/reviews/admin/assignments/bulk-delete", { ids });
      }
    }

    // Show any warnings (e.g. "all reviews on cooldown for X combo")
    if (allWarnings.length) {
      saveWarnings.value = allWarnings;
    }

    emit("update:modelValue", false);
    emit("saved");
  } catch (err) {
    console.error("Failed to save:", err);
    saveError.value = err.response?.data?.error || "Failed to save assignment";
    // Show warnings from the error response (e.g. "no reviews found for combo")
    if (err.response?.data?.warnings?.length) {
      saveWarnings.value = err.response.data.warnings;
    }
  } finally {
    saving.value = false;
  }
}
</script>
